import { supabase } from '@/shared/config/supabase';
import type { IEmailRepository, SendEmailParams, EmailLogFilter, EmailStats } from '../../domain/repositories/IEmailRepository';
import { EmailTemplate, type EmailCategory } from '../../domain/entities/EmailTemplate';
import { EmailLog, type EmailStatus } from '../../domain/entities/EmailLog';

interface TemplateRow {
  id: string;
  name: string;
  description: string | null;
  subject: string;
  html_content: string;
  variables: string[];
  category: string;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  workspace_id: number | null;
}

interface LogRow {
  id: number;
  recipient_email: string;
  subject: string;
  template_id: string | null;
  status: string;
  resend_id: string | null;
  error_message: string | null;
  sent_by: string | null;
  workspace_id: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export class SupabaseEmailRepository implements IEmailRepository {
  private mapRowToTemplate(row: TemplateRow): EmailTemplate {
    return new EmailTemplate(
      row.id,
      row.name,
      row.description,
      row.subject,
      row.html_content,
      row.variables || [],
      row.category as EmailCategory,
      row.is_active,
      row.is_system,
      new Date(row.created_at),
      new Date(row.updated_at),
      row.created_by,
      row.workspace_id
    );
  }

  private mapRowToLog(row: LogRow): EmailLog {
    return new EmailLog(
      row.id,
      row.recipient_email,
      row.subject,
      row.template_id,
      row.status as EmailStatus,
      row.resend_id,
      row.error_message,
      row.sent_by,
      row.workspace_id,
      row.metadata || {},
      new Date(row.created_at)
    );
  }

  // Templates
  async getTemplates(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('category')
      .order('name');

    if (error) throw new Error(`Failed to fetch templates: ${error.message}`);
    return (data || []).map(row => this.mapRowToTemplate(row));
  }

  async getTemplateById(id: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch template: ${error.message}`);
    }
    return this.mapRowToTemplate(data);
  }

  async getTemplatesByCategory(category: EmailCategory): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('category', category)
      .eq('is_active', true)
      .order('name');

    if (error) throw new Error(`Failed to fetch templates: ${error.message}`);
    return (data || []).map(row => this.mapRowToTemplate(row));
  }

  async createTemplate(template: Omit<EmailTemplate, 'createdAt' | 'updatedAt'>): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .insert({
        id: template.id,
        name: template.name,
        description: template.description,
        subject: template.subject,
        html_content: template.htmlContent,
        variables: template.variables,
        category: template.category,
        is_active: template.isActive,
        is_system: template.isSystem,
        created_by: template.createdBy,
        workspace_id: template.workspaceId,
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to create template: ${error.message}`);
    return this.mapRowToTemplate(data);
  }

  async updateTemplate(id: string, updates: Partial<EmailTemplate>): Promise<EmailTemplate> {
    const updateData: Record<string, unknown> = {};

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.subject !== undefined) updateData.subject = updates.subject;
    if (updates.htmlContent !== undefined) updateData.html_content = updates.htmlContent;
    if (updates.variables !== undefined) updateData.variables = updates.variables;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      .from('email_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(`Failed to update template: ${error.message}`);
    return this.mapRowToTemplate(data);
  }

  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id)
      .eq('is_system', false); // Prevent deleting system templates

    if (error) throw new Error(`Failed to delete template: ${error.message}`);
  }

  // Logs
  async getLogs(filter?: EmailLogFilter): Promise<EmailLog[]> {
    let query = supabase
      .from('email_logs')
      .select('*')
      .order('created_at', { ascending: false });

    if (filter?.status) {
      query = query.eq('status', filter.status);
    }
    if (filter?.templateId) {
      query = query.eq('template_id', filter.templateId);
    }
    if (filter?.recipientEmail) {
      query = query.ilike('recipient_email', `%${filter.recipientEmail}%`);
    }
    if (filter?.fromDate) {
      query = query.gte('created_at', filter.fromDate.toISOString());
    }
    if (filter?.toDate) {
      query = query.lte('created_at', filter.toDate.toISOString());
    }
    if (filter?.limit) {
      query = query.limit(filter.limit);
    }
    if (filter?.offset) {
      query = query.range(filter.offset, filter.offset + (filter.limit || 50) - 1);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to fetch logs: ${error.message}`);
    return (data || []).map(row => this.mapRowToLog(row));
  }

  async getLogById(id: number): Promise<EmailLog | null> {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch log: ${error.message}`);
    }
    return this.mapRowToLog(data);
  }

  async getStats(workspaceId?: number): Promise<EmailStats> {
    let query = supabase.from('email_logs').select('status');

    if (workspaceId) {
      query = query.eq('workspace_id', workspaceId);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to fetch stats: ${error.message}`);

    const stats: EmailStats = {
      total: 0,
      sent: 0,
      failed: 0,
      pending: 0,
      bounced: 0,
    };

    (data || []).forEach(row => {
      stats.total++;
      const status = row.status as keyof typeof stats;
      if (status in stats) {
        stats[status]++;
      }
    });

    return stats;
  }

  // Sending
  async sendEmail(params: SendEmailParams): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Edge function error:', data);
        return { success: false, error: data.error || data.details || 'Error sending email' };
      }

      return {
        success: data.success || false,
        messageId: data.messageId,
        error: data.error,
      };
    } catch (err) {
      console.error('Exception calling Edge function:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  }

  async sendWelcomeEmail(
    _userId: string,
    userEmail: string,
    userName: string,
    userRole: string,
    workspaceId: number
  ): Promise<{ success: boolean; error?: string }> {
    // Get workspace name
    const { data: workspace } = await supabase
      .from('Workspaces')
      .select('name')
      .eq('id', workspaceId)
      .single();

    const workspaceName = workspace?.name || 'Coursue';
    const loginUrl = `${window.location.origin}/login`;

    return this.sendEmail({
      to: userEmail,
      templateId: 'welcome',
      templateData: {
        user_name: userName,
        user_email: userEmail,
        user_role: this.translateRole(userRole),
        workspace_name: workspaceName,
        login_url: loginUrl,
      },
    });
  }

  private translateRole(role: string): string {
    const roles: Record<string, string> = {
      DIRECTOR: 'Director',
      TEACHER: 'Profesor',
      STUDENT: 'Estudiante',
      ADMIN: 'Administrador',
    };
    return roles[role.toUpperCase()] || role;
  }

  async processPendingEmails(): Promise<{ processed: number; errors: number }> {
    // Get pending emails
    const { data: pendingEmails, error } = await supabase
      .from('email_logs')
      .select('*')
      .eq('status', 'pending')
      .order('created_at')
      .limit(50);

    if (error || !pendingEmails) {
      return { processed: 0, errors: 0 };
    }

    let processed = 0;
    let errors = 0;

    for (const email of pendingEmails) {
      const metadata = email.metadata as Record<string, string>;
      const result = await this.sendEmail({
        to: email.recipient_email,
        templateId: email.template_id || undefined,
        templateData: metadata,
      });

      if (result.success) {
        await supabase
          .from('email_logs')
          .update({ status: 'sent', resend_id: result.messageId })
          .eq('id', email.id);
        processed++;
      } else {
        await supabase
          .from('email_logs')
          .update({ status: 'failed', error_message: result.error })
          .eq('id', email.id);
        errors++;
      }
    }

    return { processed, errors };
  }
}
